import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Shield,
  Loader2,
  Upload,
  FileText,
  Plus,
  Trash2,
  Eye,
  Edit2,
  Sparkles,
  X,
  GripVertical,
  Save,
  Type,
  Hash,
  Calendar,
  ListChecks,
  ToggleLeft,
  AlignLeft,
  CheckSquare,
  Square
} from "lucide-react";

// Tipos de campo disponibles
const FIELD_TYPES = [
  { value: "text", label: "Texto", icon: Type },
  { value: "number", label: "Número", icon: Hash },
  { value: "date", label: "Fecha", icon: Calendar },
  { value: "select", label: "Lista de opciones", icon: ListChecks },
  { value: "checkbox", label: "Sí/No", icon: ToggleLeft },
  { value: "textarea", label: "Texto largo", icon: AlignLeft }
];

export default function CoberturasPolizasPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [processingFile, setProcessingFile] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  
  // Form state for new/edit plantilla
  const [formData, setFormData] = useState({
    nombre_ramo: "",
    campos: []
  });
  
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  const fetchPlantillas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coberturas-polizas`, getAuthHeaders());
      setPlantillas(res.data);
    } catch (err) {
      console.error("Error fetching plantillas:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPlantillas();
  }, [fetchPlantillas]);

  const resetForm = () => {
    setFormData({
      nombre_ramo: "",
      campos: []
    });
    setEditingPlantilla(null);
  };

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allIds = new Set(plantillas.map(p => p.id));
    setSelectedItems(allIds);
  };

  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelected = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/coberturas-polizas/${id}`, getAuthHeaders());
        successCount++;
      } catch (error) {
        console.error(`Error deleting plantilla ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} plantilla(s) eliminada(s) exitosamente`);
      fetchPlantillas();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} plantilla(s)`);
    }

    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      nombre_ramo: plantilla.nombre_ramo,
      campos: plantilla.campos || []
    });
    setShowDialog(true);
  };

  const openViewDialog = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setShowViewDialog(true);
  };

  // Handle file upload and AI extraction
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await processFile(file);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  // Process the uploaded file
  const processFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato no soportado. Use PDF, Word o imagen.");
      return;
    }

    // Max 20MB
    if (file.size > 20 * 1024 * 1024) {
      toast.error("El archivo no debe superar 20MB");
      return;
    }

    setProcessingFile(true);
    toast.info("Procesando documento con IA...");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(
        `${API}/coberturas-polizas/extract-from-file`,
        formDataUpload,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (res.data.campos && res.data.campos.length > 0) {
        setFormData(prev => ({
          ...prev,
          nombre_ramo: res.data.nombre_ramo || prev.nombre_ramo,
          campos: res.data.campos
        }));
        toast.success(`Se extrajeron ${res.data.campos.length} campos del documento`);
      } else {
        toast.warning("No se pudieron extraer campos del documento");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al procesar el documento";
      toast.error(errorMsg);
    } finally {
      setProcessingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Remove a campo from the list
  const removeCampo = (index) => {
    setFormData(prev => ({
      ...prev,
      campos: prev.campos.filter((_, i) => i !== index)
    }));
  };

  // Update a campo value
  const updateCampoValue = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      campos: prev.campos.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  // Add a new empty campo
  const addCampo = () => {
    setFormData(prev => ({
      ...prev,
      campos: [...prev.campos, { nombre: "", tipo: "text", valor: "" }]
    }));
  };

  // Get field type label
  const getFieldTypeLabel = (tipo) => {
    const fieldType = FIELD_TYPES.find(ft => ft.value === tipo);
    return fieldType?.label || "Texto";
  };

  // Get field type icon
  const getFieldTypeIcon = (tipo) => {
    const fieldType = FIELD_TYPES.find(ft => ft.value === tipo);
    return fieldType?.icon || Type;
  };

  // Save plantilla
  const handleSubmit = async () => {
    if (!formData.nombre_ramo.trim()) {
      toast.error("El Nombre del Ramo es obligatorio");
      return;
    }

    if (formData.campos.length === 0) {
      toast.error("Debe agregar al menos un campo a la plantilla");
      return;
    }

    try {
      if (editingPlantilla) {
        await axios.put(
          `${API}/coberturas-polizas/${editingPlantilla.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/coberturas-polizas`, formData, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchPlantillas();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al guardar";
      toast.error(errorMsg);
    }
  };

  // Delete plantilla
  const deletePlantilla = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta plantilla?")) return;
    
    try {
      await axios.delete(`${API}/coberturas-polizas/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="coberturas-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0C314C] dark:text-white flex items-center gap-3">
              <Shield className="w-8 h-8" />
              Cobertura de Pólizas
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Cree plantillas de cobertura a partir de documentos para comparaciones fijas
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Multi-selection controls */}
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {selectedItems.size} seleccionados
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="select-all-coberturas-button"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="deselect-all-coberturas-button"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Ninguno
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.size === 0 || deletingMultiple}
                  data-testid="delete-selected-coberturas-button"
                >
                  {deletingMultiple ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Eliminar ({selectedItems.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectionMode}
                  data-testid="cancel-selection-coberturas-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                {plantillas.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={toggleSelectionMode}
                    className="text-slate-600 dark:text-slate-300"
                    data-testid="enable-selection-coberturas-button"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Seleccionar
                  </Button>
                )}
                <Button 
                  onClick={openNewDialog}
                  className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white font-medium shadow-md"
                  data-testid="new-cobertura-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Plantilla
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100">Total Plantillas</p>
                  <p className="text-3xl font-bold">{plantillas.length}</p>
                </div>
                <Shield className="w-12 h-12 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plantillas List */}
        {plantillas.length === 0 ? (
          <Card className="p-12 text-center">
            <Shield className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 dark:text-slate-300 mb-2">
              No hay plantillas de cobertura
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              Cargue un documento de póliza para que la IA extraiga los campos automáticamente
            </p>
            <Button onClick={openNewDialog} className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Crear Primera Plantilla
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plantillas.map((plantilla) => (
              <Card 
                key={plantilla.id} 
                className={`hover:shadow-lg transition-shadow dark:bg-slate-800 relative ${
                  isSelectionMode && selectedItems.has(plantilla.id) ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                data-testid={`plantilla-card-${plantilla.id}`}
              >
                {/* Selection checkbox */}
                {isSelectionMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox
                      checked={selectedItems.has(plantilla.id)}
                      onCheckedChange={() => toggleItemSelection(plantilla.id)}
                      className="w-5 h-5 bg-white dark:bg-slate-700 border-2"
                      data-testid={`checkbox-cobertura-${plantilla.id}`}
                    />
                  </div>
                )}
                <CardHeader className={`pb-3 ${isSelectionMode ? 'pl-12' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-slate-800 dark:text-white">
                          {plantilla.nombre_ramo}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          {plantilla.campos?.length || 0} campos
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Preview of first 3 campos */}
                  <div className="space-y-2 mb-4">
                    {(plantilla.campos || []).slice(0, 3).map((campo, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500 dark:text-slate-400 truncate w-1/3">
                          {campo.nombre}:
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1">
                          {campo.valor || "-"}
                        </span>
                      </div>
                    ))}
                    {(plantilla.campos?.length || 0) > 3 && (
                      <p className="text-xs text-slate-400">
                        +{plantilla.campos.length - 3} campos más...
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openViewDialog(plantilla)}
                      className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(plantilla)}
                      className="border-amber-300 text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deletePlantilla(plantilla.id)}
                      className="border-red-300 text-red-500 hover:text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0C314C]" />
                {editingPlantilla ? "Editar Plantilla de Cobertura" : "Nueva Plantilla de Cobertura"}
              </DialogTitle>
              <DialogDescription>
                {editingPlantilla 
                  ? "Modifique los campos de la plantilla según sea necesario"
                  : "Cargue un documento para que la IA extraiga los campos automáticamente, o agréguelos manualmente"
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* File Upload Section */}
              {!editingPlantilla && (
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                    isDragging 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-[1.02]' 
                      : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer block">
                    <div className="flex flex-col items-center gap-3">
                      {processingFile ? (
                        <>
                          <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                          <p className="text-slate-600 dark:text-slate-300">Procesando documento con IA...</p>
                        </>
                      ) : isDragging ? (
                        <>
                          <div className="p-3 rounded-full bg-blue-200 dark:bg-blue-800/50 animate-pulse">
                            <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                          </div>
                          <p className="text-blue-600 dark:text-blue-400 font-medium">
                            Suelte el archivo aquí
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                            <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-slate-600 dark:text-slate-300 font-medium">
                              Haga clic o arrastre un documento aquí
                            </p>
                            <p className="text-sm text-slate-400">
                              PDF, Word o imagen (máx. 20MB)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-sm text-blue-600 dark:text-blue-400">
                            <Sparkles className="w-4 h-4" />
                            <span>La IA extraerá los campos automáticamente</span>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* Nombre del Ramo */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Nombre del Ramo *
                  <span className="text-xs text-slate-400">(Identificador de la plantilla)</span>
                </Label>
                <Input
                  value={formData.nombre_ramo}
                  onChange={(e) => setFormData({ ...formData, nombre_ramo: e.target.value })}
                  placeholder="Ej: Automóvil, Hogar, Vida..."
                  className="dark:bg-slate-700 dark:border-slate-600"
                  data-testid="nombre-ramo-input"
                />
              </div>

              {/* Campos List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-900 dark:text-white font-medium">Campos de la Plantilla</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCampo}
                    className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C] hover:text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar Campo
                  </Button>
                </div>

                {formData.campos.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No hay campos. Cargue un documento o agregue campos manualmente.</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[300px]">
                    <div className="space-y-3 pr-4">
                      {/* Header row */}
                      <div className="flex items-center gap-3 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300">
                        <div className="w-4" />
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <span>Nombre del Campo</span>
                          <span>Tipo de Campo</span>
                          <span>Valor por Defecto (opcional)</span>
                        </div>
                        <div className="w-8" />
                      </div>
                      
                      {formData.campos.map((campo, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <GripVertical className="w-4 h-4 text-slate-400 cursor-move flex-shrink-0" />
                          <div className="flex-1 grid grid-cols-3 gap-3">
                            <Input
                              value={campo.nombre}
                              onChange={(e) => updateCampoValue(index, "nombre", e.target.value)}
                              placeholder="Ej: Suma Asegurada"
                              className="dark:bg-slate-700 dark:border-slate-600"
                            />
                            <Select
                              value={campo.tipo || "text"}
                              onValueChange={(value) => updateCampoValue(index, "tipo", value)}
                            >
                              <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                                <SelectValue placeholder="Seleccionar tipo" />
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPES.map((type) => {
                                  const Icon = type.icon;
                                  return (
                                    <SelectItem key={type.value} value={type.value}>
                                      <div className="flex items-center gap-2">
                                        <Icon className="w-4 h-4 text-slate-500" />
                                        {type.label}
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <Input
                              value={campo.valor || ""}
                              onChange={(e) => updateCampoValue(index, "valor", e.target.value)}
                              placeholder="Valor por defecto"
                              className="dark:bg-slate-700 dark:border-slate-600"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCampo(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-slate-300">
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit}
                className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white"
                disabled={!formData.nombre_ramo || formData.campos.length === 0}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingPlantilla ? "Actualizar" : "Guardar"} Plantilla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0C314C]" />
                {selectedPlantilla?.nombre_ramo}
              </DialogTitle>
              <DialogDescription>
                Plantilla de cobertura con {selectedPlantilla?.campos?.length || 0} campos
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="space-y-3">
                {(selectedPlantilla?.campos || []).map((campo, index) => {
                  const FieldIcon = getFieldTypeIcon(campo.tipo);
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <Badge variant="outline" className="shrink-0 mt-0.5 bg-[#0C314C] text-white border-0">
                        {index + 1}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {campo.nombre}
                        </p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
                            <FieldIcon className="w-3 h-3" />
                            {getFieldTypeLabel(campo.tipo)}
                          </span>
                          {campo.valor && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Valor: <span className="text-slate-700 dark:text-slate-300">{campo.valor}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowViewDialog(false)} className="border-slate-300">
                Cerrar
              </Button>
              <Button 
                onClick={() => {
                  setShowViewDialog(false);
                  openEditDialog(selectedPlantilla);
                }}
                className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar {selectedItems.size} plantilla(s) de cobertura? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelected}
                disabled={deletingMultiple}
                data-testid="confirm-delete-coberturas-button"
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
